import React, { useContext, useEffect, useRef, useState } from 'react';
import FadeIn from 'react-fade-in';
import { useObserver } from 'mobx-react-lite';
import momentLocalizer from 'react-widgets-moment';
import { GlobalHotKeys } from 'react-hotkeys';
import moment from 'moment';

import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModal';
import ServiceAttributeUpdateStore from '../../../../stores/ServiceAttributeUpdateStore';
import ServiceAttributeSearchStore from '../../../../stores/ServiceAttributeSearchStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';

moment.locale('en');
momentLocalizer();

function DeleteService(props) {
    const isMounted = useRef(true);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const serviceAttribute = useContext(ServiceAttributeUpdateStore);
    const search = useContext(ServiceAttributeSearchStore);
    const quickDrawer = useContext(QuickDrawerStore);

    useEffect(() => {
        quickDrawerFocus(props.drawer);
        return () => { isMounted.current = false; }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            // Same pattern as other drawers; check unsaved changes just in case
            if (serviceAttribute.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        setConfirmDelete(true);
    }

    const handleConfirmDelete = async (event) => {
        try {
            const id = serviceAttribute.serviceId || (serviceAttribute.data ? serviceAttribute.data.serviceId : null);
            if (id) {
                await search.delete([id]);
            }
            if (isMounted.current && props.onSuccess && fn.isFunction(props.onSuccess)) {
                props.onSuccess(event, 'deleted');
            }
        } finally {
            if (isMounted.current) {
                setConfirmDelete(false);
            }
        }
    }

    const handleConfirmCancel = (event) => {
        setConfirmDelete(false);
        if (props.onCancel && fn.isFunction(props.onCancel)) {
            props.onCancel(event);
        }
    }

    const renderBody = () => {
        const data = serviceAttribute.data;
        if (!data) return renderQuickDrawerLoading();

        return <FadeIn>
            <div className='delete-service body-content'>
                <section className='service-info'>
                    <div className='row'>
                        <div className='col-12'>
                            <label><small>Service</small></label>
                            <h4 className='m-0 text-truncate' title={data.name}><strong>{data.name}</strong></h4>
                            <small className='text-gray-700'>Code: <strong>{data.code}</strong></small>
                        </div>
                    </div>
                </section>
                <section className='service-flags'>
                    <div className='row'>
                        <div className='col-12'>
                            <label><small>Details</small></label>
                            <ul className='leaders'>
                                <li><span>Fee</span><span>{fn.formatCurrency(data.fee, '--')}</span></li>
                                <li><span>Bookable</span><span>{data.isBookable ? 'Yes' : 'No'}</span></li>
                                <li><span>Repeatable</span><span>{data.isRepeatable ? 'Yes' : 'No'}</span></li>
                                <li><span>Taxable</span><span>{data.isTaxable ? 'Yes' : 'No'}</span></li>
                                <li><span>Default Duration</span><span>{data.defaultDurationInMinutes ? `${data.defaultDurationInMinutes} min` : '--'}</span></li>
                            </ul>
                        </div>
                    </div>
                </section>
            </div>
        </FadeIn>
    }

    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) && !confirmDelete ?
                    <GlobalHotKeys
                        keyMap={{ close: ['esc'] }}
                        handlers={{ close: event => { props.onCancel(event) } }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form onSubmit={handleSubmit}>
            <fieldset disabled={search.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('service', 'delete')}
                        action='Delete'
                        category='Service'
                        className='delete'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        {serviceAttribute.isReady ? renderBody() : renderQuickDrawerLoading()}
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button type='button' className='btn btn-link btn-cancel mr-2' onClick={handleCancel}>Cancel</button>
                                    <button type='submit' className='btn btn-danger'>Delete</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
        <BodyEnd>
            <ConfirmModal
                icon={<i className={'text-danger mr-2 ' + oh.getIcon('service', 'delete')}></i>}
                message={<>Continue to delete <span className='fw-500'>&nbsp;{serviceAttribute.data ? serviceAttribute.data.name : ''}</span>?</>}
                descriptionClassName='warning-danger warning-flashing'
                show={confirmDelete}
                onOption1Click={handleConfirmDelete}
                onCancel={handleConfirmCancel}
            />
        </BodyEnd>
    </>)
}

export default DeleteService;

